import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { BsMic, BsImages, BsChatText, BsGift } from 'react-icons/bs';
import { IoVideocamOutline } from 'react-icons/io5';
import { patch } from 'utils/api';
import useSWR from 'swr';
import { redeemGiftCertificate } from 'account-dashboard/api';
import { serverErrorMessage } from 'account-dashboard/hooks';
import { PrimaryButton, ErrorText } from 'account-dashboard/styled';
import { colors, fonts } from 'account-dashboard/tokens';
import bearImage from '../assets/redeem-bear.png';
import sunImage from '../assets/redeem-sun.svg';

// "Open my gift" — the screen the recipient lands on once their account exists and
// the certificate has been claimed. Pressing the button is what REDEEMS the gift:
// redemption is deliberately not automatic on mount, since claiming (during
// signup) and redeeming (an explicit act of opening the gift) are separate
// operations.
const Screen = styled.div`
  background: #fff8e1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Art = styled.div`
  position: relative;
  width: 100%;
  max-width: 353px;
    margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
`;

const Sun = styled.img`
  position: absolute;
  bottom: -90px;
  width: 319px;
  max-width: 100%;
  z-index: 1;
  pointer-events: none;
`;

const Bear = styled.img`
  position: relative;
  width: 330px;
  z-index: 3;
  max-width: 100%;
  object-fit: contain;
  pointer-events: none;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${colors.chipBorder};
  border-radius: 16px;
  padding: 16px;
  padding-top: 90px;
  width: 100%;
  max-width: 353px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  margin: 0 0 40px;
  margin-top: -70px;
  z-index: 2;
`;

const Title = styled.h1`
  font-family: ${fonts.heading};
  font-weight: 500;
  font-size: 30px;
  line-height: 1.2;
  letter-spacing: -0.18px;
  color: ${colors.text};
  text-align: center;
  margin: 0;
`;

const Caption = styled.p`
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: -0.096px;
  color: ${colors.text};
  text-align: center;
  margin: 0;
`;

const Includes = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const IncludeTile = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-family: ${fonts.body};
  font-size: 16px;
  letter-spacing: -0.096px;
  color: ${colors.text};
`;

const INCLUDES = [
  { key: 'audio', icon: BsMic, label: 'Audio' },
  { key: 'video', icon: IoVideocamOutline, label: 'Video' },
  { key: 'photos', icon: BsImages, label: 'Photos' },
  { key: 'stories', icon: BsChatText, label: 'Stories' },
];

const OpenMyGift = ({ couponCode, onRedeemed }) => {
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [error, setError] = useState(null);

  // The giver's name for the title comes from the certificate itself, read with
  // the same lookup-by-code endpoint the rest of the redeem flow uses. This is a
  // read only — it never redeems.
  const { data } = useSWR(couponCode ? `/v3/commerce/gift_certificates/${couponCode}` : null);
  const giverName = data?.data?.data?.attributes?.['name-of-giver'];

  const title = giverName
    ? `${giverName} sent you a baby year book!`
    : 'You have been sent a baby year book!';

  const onOpen = async () => {
    if (isRedeeming || !couponCode) return;
    setIsRedeeming(true);
    setError(null);

    try {
      // Both requests share this try/finally so a failure in either one clears
      // isRedeeming and shows the error, instead of leaving the button stuck on
      // "Opening your gift" forever (what happened when this claim call sat
      // ahead of the try, unguarded).
      await patch(`/v3/commerce/gift_certificates/${couponCode}/claim`);

      const response = await redeemGiftCertificate(couponCode);
      // The response is the source of truth for the redeemed certificate and the
      // journal that just transferred — hand it straight to the caller.
      onRedeemed(response);
    } catch (err) {
      // Stay on this screen; never advance to the journal on a failed redemption.
      setError(err);
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <Screen>
      <Art>
        <Sun src={sunImage} alt="" />
        <Bear src={bearImage} alt="" />
      </Art>

      <Card>
        <Title>{title}</Title>
        <Caption>Your gift includes</Caption>
        <Includes>
          {INCLUDES.map(({ key, icon: Icon, label }) => (
            <IncludeTile key={key}>
              <Icon size={30} />
              {label}
            </IncludeTile>
          ))}
        </Includes>
        <Caption>In a beautifully designed keepsake book</Caption>

        {!!error && (
          <ErrorText>
            {serverErrorMessage(
              error,
              error?.response?.status === 410
                ? "We couldn't find that gift certificate. Please check the code and try again."
                : "We couldn't open your gift just yet. Please try again."
            )}
          </ErrorText>
        )}

        <PrimaryButton type="button" disabled={isRedeeming} onClick={onOpen}>
          <BsGift size={20} />
          {isRedeeming ? 'Opening your gift' : 'Open my gift!'}
        </PrimaryButton>
      </Card>
    </Screen>
  );
};

OpenMyGift.propTypes = {
  couponCode: PropTypes.string,
  onRedeemed: PropTypes.func.isRequired,
};

OpenMyGift.defaultProps = {
  couponCode: null,
};

export default OpenMyGift;
